import { Content, ContentValueLocalizable, RelationValue, Localized, ContentValue } from "@robe/core-types";
import {
  ContentUnfetchedValuesFieldsObject,
  UnresolvedUnfetchedFieldValue,
  LocalizedUnresolvedUnfetchedFieldValue,
} from "./resolveUnfetchedFields";

interface MapFetchedContentRelationsToContentProps {
  relationSourceFields: ContentUnfetchedValuesFieldsObject[];
  fetchedRelationsContents: Content[];
}

type ContentValuesEntriesObject = Localized<Content> | Content | Content[] | Localized<Content[]>;
type ContentValuesEntries = [string, ContentValuesEntriesObject | Record<string, ContentValuesEntriesObject>[]][];

/**
 * Maps fetched relations to a content object by updating its unfetched relation fields.
 *
 * @param relationSourceFields - The list of fields that represent the relations to be fetched from the content.
 * @param fetchedRelationsContents - The array of fetched content relations to map to the content.
 *
 * @returns A function that takes a content and maps the fetched relation data to its values.
 */
export const mapFetchedContentRelationsToContent =
  ({ relationSourceFields, fetchedRelationsContents }: MapFetchedContentRelationsToContentProps) =>
    (content: Content) => {
      if (!content.values) {
        return content;
      }

      // Do not map fields that aren't present in current content
      const relationSourceFieldsPresentInContent = relationSourceFields.filter(({ field, ofListObjectField }) =>
        ofListObjectField ? ofListObjectField in content.values : field in content.values
      );

      if (!relationSourceFieldsPresentInContent || relationSourceFieldsPresentInContent.length === 0) {
        return content;
      }

      const values = content.values as Record<string, ContentValueLocalizable>;

      const entries = mapFetchedContentRelationsToContentValuesEntries({
        values,
        relationSourceFields: relationSourceFieldsPresentInContent,
        fetchedRelationsContents,
      });

      const objectListEntries = mergeContentValuesEntries({ entries, relationSourceFields: relationSourceFieldsPresentInContent });

      const fetchedRelationValues = Object.fromEntries([...entries, ...objectListEntries]);

      return {
        ...content,
        values: {
          ...content.values,
          ...fetchedRelationValues,
        },
      };
    };

interface MapFetchedContentRelationsToContentValuesEntriesProps {
  relationSourceFields: ContentUnfetchedValuesFieldsObject[];
  fetchedRelationsContents: Content[];
  values: Record<string, ContentValueLocalizable>;
}

const mapFetchedContentRelationsToContentValuesEntries = ({
  relationSourceFields,
  fetchedRelationsContents,
  values,
}: MapFetchedContentRelationsToContentValuesEntriesProps) =>
  relationSourceFields.map(({ field, ofListObjectField }) => {
    const fieldName = ofListObjectField ?? field;

    if (values?.[fieldName]) {
      const value = values[fieldName] as UnresolvedUnfetchedFieldValue | LocalizedUnresolvedUnfetchedFieldValue;

      if (!value) {
        return [fieldName, value];
      }

      // Multiple relation or list object
      if (Array.isArray(value)) {
        const multipleRelationOrObjectListValue = mapMultipleRelationOrObjectListValue({
          value,
          field,
          ofListObjectField,
          fetchedRelationsContents,
        });

        return [ofListObjectField ?? fieldName, multipleRelationOrObjectListValue];
      }

      if (typeof value === "object") {
        const relationFieldObject = value as ContentValueLocalizable;

        // Simple relation
        if ("id" in value) {
          const fetchedRelationContent = fetchedRelationsContents.find(({ id }) => id === (value as RelationValue)?.id);

          return [fieldName, fetchedRelationContent ?? value];
        }

        const localizedFieldValue = relationFieldObject as Localized<ContentValue>;

        // reuse existing localized object and map fetched contents
        const localizedRelation = Object.fromEntries(
          Object.entries(localizedFieldValue).map(([locale, localeValue]) => {
            if (localeValue) {
              // Localized multiple relation or list object
              if (Array.isArray(localeValue)) {
                const multipleRelationValue = mapMultipleRelationOrObjectListValue({
                  value: localeValue as RelationValue[] | Record<string, RelationValue | RelationValue[]>[],
                  field,
                  ofListObjectField,
                  fetchedRelationsContents,
                });

                return [locale, multipleRelationValue ?? localeValue];
              }

              // Localized simple relation
              if (typeof localeValue === "object" && "id" in localeValue) {
                const fetchedRelationContent = fetchedRelationsContents.find(({ id }) => id === localeValue.id);

                return [locale, fetchedRelationContent ?? localeValue];
              }
            }

            return [locale, localeValue];
          })
        );

        return [fieldName, localizedRelation];
      }
    }

    return [fieldName, values?.[fieldName]];
  }) as ContentValuesEntries;

interface MapMultipleRelationOrObjectListValueProps {
  value: RelationValue[] | Record<string, RelationValue | RelationValue[]>[];
  field: string;
  ofListObjectField?: string;
  fetchedRelationsContents: Content[];
}

const mapMultipleRelationOrObjectListValue = ({
  value,
  field,
  ofListObjectField,
  fetchedRelationsContents,
}: MapMultipleRelationOrObjectListValueProps) =>
  value.map((listValue) => {
    // List object
    if (ofListObjectField && field in listValue) {
      const listObjectFieldValue = (listValue as Record<string, RelationValue | RelationValue[]>)[field];
      if (listObjectFieldValue) {
        // Multiple relation in list object
        if (Array.isArray(listObjectFieldValue)) {
          const fetchedRelationContent = listObjectFieldValue.map((listObjectFieldValueMultipleRelationValue: RelationValue) => {
            const listObjectFieldValueMultipleRelationValueMapped = fetchedRelationsContents.find(
              ({ id }) => id === listObjectFieldValueMultipleRelationValue.id
            );

            return listObjectFieldValueMultipleRelationValueMapped ?? listObjectFieldValueMultipleRelationValue;
          });

          return {
            ...listValue,
            [field]: fetchedRelationContent,
          };
        }

        if (typeof listObjectFieldValue === "object") {
          // Simple non localized relation in list object
          if ("id" in listObjectFieldValue) {
            const fetchedRelationContent = fetchedRelationsContents.find(({ id }) => id === listObjectFieldValue.id);

            return {
              ...listValue,
              [field]: fetchedRelationContent ?? listObjectFieldValue,
            };
          }

          return {
            ...listValue,
            [field]: Object.fromEntries(
              Object.entries(listObjectFieldValue).map(([locale, relValue]) => {
                // Multiple localized relation in list object
                if (Array.isArray(relValue)) {
                  const fetchedRelationContents = relValue.map((multipleRelValue) => {
                    const fetchedRelationContent =
                      typeof multipleRelValue === "object" ? fetchedRelationsContents.find(({ id }) => id === multipleRelValue?.id) : null;

                    return fetchedRelationContent ?? multipleRelValue;
                  });

                  return [locale, fetchedRelationContents ?? relValue];
                }

                // Simple localized relation in list object
                if (relValue && typeof relValue === "object") {
                  const fetchedRelationContent = fetchedRelationsContents.find(({ id }) => id === (relValue as any)?.id);

                  return [locale, fetchedRelationContent ?? relValue];
                }

                return [locale, relValue];
              })
            ),
          };
        }
      }
    }

    // Multiple relation
    if ("id" in listValue) {
      const fetchedRelationContent = fetchedRelationsContents.find(({ id }) => id === listValue.id);

      return fetchedRelationContent ?? listValue;
    }

    return listValue;
  });

interface MergeContentValuesEntriesProps {
  relationSourceFields: ContentUnfetchedValuesFieldsObject[];
  entries: ContentValuesEntries;
}

/**
 * In cases where more then one relation field of the same object list is fetched
 * every specified field creates new entry in fetched relations object result.
 * These multiple entries of the same list object must be merged into single list object afterwards,
 * otherwise, the last entry of the same list object field key will overwrite all of the previous ones.
 */
const mergeContentValuesEntries = ({ relationSourceFields, entries }: MergeContentValuesEntriesProps) => {
  /** Find keys that are present more then once */
  const duplicateKeys = entries.reduce(
    (acc, [key]) => {
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  /** Get entries with duplicate key */
  const filteredEntries = entries.filter(([key]) => duplicateKeys[key] > 1);

  if (filteredEntries.length === 0) {
    return entries;
  }

  /** Group filtered entries by current list object key */
  const filteredEntriesGroupedByKey = filteredEntries.reduce(
    (acc, [key, value]) => {
      // If the key is already present, add the value to the list of values
      if (acc[key]) {
        acc[key].push(value as Record<string, ContentValuesEntriesObject>[]);
      } else {
        // If the key is not present, create a new entry with the value in an array
        acc[key] = [value as Record<string, ContentValuesEntriesObject>[]];
      }

      return acc;
    },
    {} as Record<string, Record<string, ContentValuesEntriesObject>[][]>
  );

  // Convert the grouped object back to an array format
  const objectListEntriesGroupedByKey = Object.entries(filteredEntriesGroupedByKey);

  /**
   * Separate fetched object list objects into separate arrays with the fetched objects only
   * and after that merge them into object list.
   * */
  const objectListEntries = objectListEntriesGroupedByKey.map(([key, value]) => {
    // Get relation source fields for current object list
    const objectListRelationSourceFields = relationSourceFields.filter(({ ofListObjectField }) => ofListObjectField === key);

    const objectListObjectsArray = value.map((objectListMappedObjectsSeparated, i) => {
      const currentObjectListObjectKey = objectListRelationSourceFields[i].field;

      const objectListCurrentObjectOnly = objectListMappedObjectsSeparated.map((objectListObject) => ({
        [currentObjectListObjectKey]: objectListObject[currentObjectListObjectKey],
      }));

      return objectListCurrentObjectOnly;
    });

    const objectListMergedObjects = objectListObjectsArray[0].map((_, i) => {
      return objectListObjectsArray.reduce((acc, objectListObject) => {
        return { ...acc, ...objectListObject[i] };
      }, {});
    });

    return [key, objectListMergedObjects];
  });

  return objectListEntries;
};
